import { useTranslation } from "react-i18next";
import { Alert, Badge, Button } from "@h2j-data-team/ui";
import type { NotAnalyzableQvd } from "@repo/shared";

import { QvdPathLabel, SpaceLink } from "./qvd-list-parts";

export function NotAnalyzableSection({
  items,
  onRetry,
  retrying,
  retriableCount,
  disabled = false,
  baseUrl,
}: {
  items: NotAnalyzableQvd[];
  onRetry: () => void;
  retrying: boolean;
  // How many of `items` have a retriable reason (429 / transient) — the reprocess
  // action only re-analyzes those.
  retriableCount: number;
  // True while the run is active (nothing to reprocess until it finishes).
  disabled?: boolean;
  baseUrl?: string | null;
}) {
  const { t } = useTranslation("qvd-analysis");
  if (items.length === 0) return null;

  const title = t("notAnalyzable.title", { count: items.length });

  return (
    <Alert
      tone="warning"
      title={title}
      action={
        <Button
          variant="outline"
          size="sm"
          loading={retrying}
          disabled={disabled || retriableCount === 0}
          onClick={onRetry}
        >
          {t("notAnalyzable.retry", { count: retriableCount })}
        </Button>
      }
    >
      <div
        role="region"
        aria-label={title}
        tabIndex={0}
        className="mt-2 max-h-60 overflow-y-auto rounded-md border border-hairline bg-surface px-3 py-1 text-ink -outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring"
      >
        <ul>
          {items.map((q) => (
            <li
              key={q.qvdIdentity}
              className="flex items-center justify-between gap-3 border-b border-hairline-soft py-1.5 last:border-b-0"
            >
              <div className="min-w-0">
                <QvdPathLabel path={q.qvdPath} />
                <div className="text-xs text-dim">
                  <SpaceLink spaceName={q.spaceName} spaceId={q.spaceId} baseUrl={baseUrl} />
                </div>
              </div>
              <Badge variant="secondary" className="shrink-0">
                {t(`coverage.reasons.${q.reason}`, q.reason)}
              </Badge>
            </li>
          ))}
        </ul>
      </div>
    </Alert>
  );
}
